'use client'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight, Mail, Chrome } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { createClient } from '@/lib/supabase/client'

export function LoginForm() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const siteUrl = process.env.NEXT_PUBLIC_APP_URL ?? window.location.origin

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${siteUrl}/api/auth/callback`,
      },
    })

    if (error) {
      setError(error.message)
    } else {
      setSent(true)
    }
    setLoading(false)
  }

  const handleGoogleOAuth = async () => {
    setError(null)
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${siteUrl}/api/auth/callback`,
      },
    })
    if (error) {
      setError(error.message)
    }
  }

  return (
    <div className="min-h-screen bg-background text-charcoal flex flex-col justify-center items-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <Link href="/" className="inline-flex items-center gap-1 mb-8">
            <span className="text-2xl font-bold">
              the<span className="text-primary">Normal</span>.ai
            </span>
          </Link>
          <h1 className="text-3xl font-bold mb-2">Welcome back</h1>
          <p className="text-warm-gray">Sign in to continue getting help</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          {sent ? (
            <div className="text-center py-4">
              <Mail className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="font-semibold text-charcoal mb-1">Check your email</p>
              <p className="text-sm text-warm-gray">
                We sent a magic link to <strong>{email}</strong>
              </p>
            </div>
          ) : (
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-lg border border-gray-300 focus:border-primary focus:outline-none transition-colors"
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 flex items-center justify-center gap-2"
                disabled={loading}
              >
                <Mail className="w-4 h-4" />
                {loading ? 'Sending…' : 'Send magic link'}
              </Button>
            </form>
          )}

          {!sent && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-warm-gray">or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleOAuth}
                className="w-full border-gray-300 text-charcoal hover:bg-gray-50 py-3 flex items-center justify-center gap-2"
              >
                <Chrome className="w-4 h-4" />
                Google
              </Button>
            </>
          )}
        </div>

        {/* Signup Link */}
        <div className="text-center">
          <p className="text-warm-gray">
            Don&apos;t have an account?{' '}
            <Link href="/signup" className="text-primary font-semibold hover:underline">
              Sign up for free
            </Link>
          </p>
        </div>

        {/* Info */}
        <div className="mt-8 pt-8 border-t border-gray-200 text-center">
          <p className="text-sm sm:text-base text-warm-gray leading-relaxed">
            We'll send you a link to sign in. No password needed. It&apos;s that simple.
          </p>
        </div>
      </div>
    </div>
  )
}
